import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  subscribeEngagement,
  loadEngagement,
  isLiked,
  isBookmarked,
  toggleLike,
  toggleBookmark,
  rememberAuthReturn,
  isSignedIn,
} from "../api/engagement";
import { paths } from "../routing/paths";

/*
 * Like & bookmark icon buttons (design system v1.1 §12) — two distinct
 * actions on classes and series: like (appreciation, feeds popularity)
 * and bookmark (save for later). 44px targets, visible at rest, never
 * hover-revealed. rest → outline; active → filled glyph + Primary Blue
 * ring + tint. `surface`: light (cards/rows), navy (hero), navyBare (the
 * player's control bar — borderless, matches the transport buttons),
 * bare (row trailing controls — borderless like the §13 rows).
 * Signed-out taps route to sign-in and return to this exact page.
 */

export const HeartGlyph = ({ filled }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke={filled ? "none" : "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 21s-8-5.3-8-11a4.5 4.5 0 0 1 8-2.8A4.5 4.5 0 0 1 20 10c0 5.7-8 11-8 11z" />
  </svg>
);
export const BookmarkGlyph = ({ filled }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke={filled ? "none" : "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 4h12v17l-6-4-6 4z" />
  </svg>
);

const STYLES = {
  light: {
    rest: "bg-transparent text-ic-body border border-ic-borderGray hover:bg-black/5 hover:border-[#B4B4B4]",
    active: "bg-[rgba(24,108,206,0.08)] text-ic-blue border border-ic-blue hover:bg-[rgba(24,108,206,0.15)]",
    focus: "focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft",
  },
  navy: {
    rest: "bg-transparent text-ic-lightGray border border-white/30 hover:bg-white/10 hover:text-white",
    active: "bg-[rgba(59,130,246,0.15)] text-ic-sky border border-ic-sky hover:bg-[rgba(59,130,246,0.25)]",
    focus: "focus-visible:outline-none focus-visible:shadow-ic-focus-dark",
  },
  navyBare: {
    rest: "bg-transparent text-ic-lightGray border-0 hover:bg-white/10 hover:text-white",
    active: "bg-transparent text-ic-sky border-0 hover:bg-white/10",
    focus: "focus-visible:outline-none focus-visible:shadow-ic-focus-dark",
  },
  bare: {
    rest: "bg-transparent text-ic-slate border-0 hover:bg-black/5 hover:text-ic-blue",
    active: "bg-transparent text-ic-blue border-0 hover:bg-[rgba(24,108,206,0.08)]",
    focus: "focus-visible:outline-none focus-visible:shadow-ic-focus-soft",
  },
};

/** Re-render on engagement-store changes. */
function useEngagementState(target, id) {
  const [, force] = useState(0);
  useEffect(() => {
    loadEngagement();
    return subscribeEngagement(() => force((n) => n + 1));
  }, []);
  return { liked: isLiked(target, id), bookmarked: isBookmarked(target, id) };
}

function ActionButton({ active, surface, labels, glyph: Glyph, onToggle }) {
  const s = STYLES[surface] || STYLES.light;
  return (
    <button
      type="button"
      aria-label={active ? labels.active : labels.rest}
      aria-pressed={active}
      onClick={onToggle}
      className={`flex-shrink-0 w-11 h-11 rounded-full inline-flex items-center justify-center cursor-pointer transition-all duration-150 ease-out ${active ? s.active : s.rest} ${s.focus}`}
    >
      <Glyph filled={active} />
    </button>
  );
}

export function useAuthGate() {
  const navigate = useNavigate();
  const location = useLocation();
  return (action) => {
    if (!isSignedIn()) {
      rememberAuthReturn(location.pathname + location.search);
      navigate(paths.signIn());
      return;
    }
    action();
  };
}

/** State + auth-gated toggles, for surfaces that render their own controls (the player's ⋯ menu). */
export function useEngagementToggles(target, id) {
  const { liked, bookmarked } = useEngagementState(target, id);
  const gate = useAuthGate();
  return {
    liked,
    bookmarked,
    toggleLike: () => gate(() => toggleLike(target, id)),
    toggleBookmark: () => gate(() => toggleBookmark(target, id)),
  };
}

export function LikeButton({ target = "class", id, surface = "light" }) {
  const { liked } = useEngagementState(target, id);
  const gate = useAuthGate();
  const noun = target === "series" ? "series" : "class";
  return (
    <ActionButton
      active={liked}
      surface={surface}
      labels={{ rest: `Like this ${noun}`, active: `Unlike this ${noun}` }}
      glyph={HeartGlyph}
      onToggle={() => gate(() => toggleLike(target, id))}
    />
  );
}

export function BookmarkButton({ target = "class", id, surface = "light" }) {
  const { bookmarked } = useEngagementState(target, id);
  const gate = useAuthGate();
  const noun = target === "series" ? "series" : "class";
  return (
    <ActionButton
      active={bookmarked}
      surface={surface}
      labels={{ rest: `Bookmark this ${noun}`, active: "Remove bookmark" }}
      glyph={BookmarkGlyph}
      onToggle={() => gate(() => toggleBookmark(target, id))}
    />
  );
}
